import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { auth, setAuthToken } from '../../api';
import { useLanguage } from '../../LanguageContext';
import { getTranslation } from '../../translations';

const Input = ({ label, className = "", ...props }) => (
  <div className="flex flex-col gap-1.5 w-full">
    {label && (
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">
        {label} {props.required && <span className="text-rose-500">*</span>}
      </label>
    )}
    <input 
      className={`
        flex h-12 w-full rounded-2xl border border-transparent bg-slate-100 px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400
        focus:outline-none focus:bg-white focus:border-[#10056E] focus:ring-4 focus:ring-[#10056E]/10 transition-all duration-200
        disabled:cursor-not-allowed disabled:opacity-50
        ${className}
      `}
      {...props}
    />
  </div>
);

const LoginScreen = ({ onLogin }) => {
    const { language } = useLanguage();
    const t = getTranslation(language);
    const [view, setView] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [resetStep, setResetStep] = useState(1);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            if (view === 'login') {
                const res = await auth.login(email.trim(), password);
                setAuthToken(res.access_token);
                if (rememberMe) {
                    localStorage.setItem('rememberedEmail', email);
                } else {
                    localStorage.removeItem('rememberedEmail');
                }
                const me = await auth.me();
                onLogin(me);
            } else if (view === 'forgot') {
                if (resetStep === 1) {
                    await auth.requestPasswordReset(email.trim());
                    alert(t('auth.codeSent'));
                    setResetStep(2);
                } else {
                    await auth.verifyPasswordReset(email.trim(), verificationCode.trim(), newPassword);
                    alert(t('auth.passwordReset'));
                    setView('login');
                    setResetStep(1);
                    setPassword('');
                    setNewPassword('');
                    setVerificationCode('');
                }
            }
        } catch (err) {
            console.error("Login Error:", err);
            let msg = t('auth.networkError');
            if (err.response) {
                msg = err.response.data?.detail || `${t('auth.serverError')} (${err.response.status})`;
            } else if (err.request) {
                msg = t('auth.noResponse');
            } else {
                msg = err.message;
            }
            alert(`${view === 'login' ? t('auth.loginFailed') : t('auth.resetFailed')}: ${msg}`);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const remembered = localStorage.getItem('rememberedEmail');
        if (remembered) {
            setEmail(remembered);
            setRememberMe(true);
        }
    }, []);

    return (
        <div className="min-h-[100dvh] w-full flex font-sans safe-area-inset">
            {/* Left: Background Image */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <img 
                    src="/login-bg.png" 
                    alt="Warehouse" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-slate-900/10" />
                <div className="relative z-10 flex flex-col justify-end p-10 pb-14">
                    <img src="/logo-main.png" alt="ENHIVE by POMEN" className="w-44 mb-5 brightness-0 invert" />
                    <p className="text-white/90 text-lg font-medium max-w-sm leading-relaxed">
                        {view === 'forgot' ? t('auth.resetYourPassword') : t('auth.subtitle')}
                    </p>
                </div>
            </div>

            {/* Right: Login Form */}
            <div className="flex-1 flex flex-col bg-white">
                <div className="flex-1 flex items-center justify-center px-6 py-8 sm:px-10">
                    <div className="w-full max-w-sm">
                        {/* Logo */}
                        <div className="text-center lg:text-left mb-8 lg:mb-10">
                            <img src="/logo-main.png" alt="ENHIVE by POMEN" className="w-48 mx-auto lg:mx-0 mb-3" />
                            <p className="text-slate-500 text-sm font-medium lg:hidden">
                                {view === 'forgot' ? t('auth.resetYourPassword') : (
                                    <>The Inventory Management Software<br />Tailored for Your Warehouse</>
                                )}
                            </p>
                            <p className="text-slate-500 text-sm hidden lg:block">Enter your credentials to access your account</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                            <Input label={t('auth.email')} type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder={t('auth.emailPlaceholder')} />
                            {view === 'login' && (
                                <>
                                    <Input label={t('auth.password')} type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••" />
                                    <div className="flex items-center justify-between">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                                className="w-4 h-4 rounded border-slate-300 text-[#10056E] focus:ring-[#10056E]"
                                            />
                                            <span className="text-sm text-slate-600">{t('auth.rememberMe')}</span>
                                        </label>
                                        <button 
                                            type="button"
                                            onClick={() => { setView('forgot'); setPassword(''); setNewPassword(''); }} 
                                            className="text-sm font-semibold text-[#10056E] hover:text-[#0D0459] transition-colors"
                                        >
                                            {t('auth.forgotPassword')}
                                        </button>
                                    </div>
                                </>
                            )}
                            {view === 'forgot' && resetStep === 2 && (
                                <>
                                    <Input 
                                        label={t('auth.verificationCode')} type="text" value={verificationCode} 
                                        onChange={e => setVerificationCode(e.target.value.toUpperCase())} 
                                        required placeholder={t('auth.enterCode')} maxLength="6"
                                    />
                                    <Input 
                                        label={t('auth.newPassword')} type="password" value={newPassword} 
                                        onChange={e => setNewPassword(e.target.value)} 
                                        required placeholder="••••••••" 
                                    />
                                </>
                            )}
                            
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="flex items-center justify-center w-full mt-4 sm:mt-6 py-3.5 sm:py-4 text-base font-semibold text-white bg-[#10056E] hover:bg-[#0D0459] rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]"
                                data-testid="login-submit-btn"
                            >
                                {isLoading ? <Loader2 size={22} className="animate-spin" /> : (
                                    view === 'login' ? t('auth.login') : 
                                    resetStep === 1 ? t('auth.sendVerificationCode') : t('auth.resetPassword')
                                )}
                            </button>
                        </form>
                        {view === 'forgot' && (
                            <div className="mt-6 text-center">
                                <button onClick={() => { setView('login'); setResetStep(1); setPassword(''); setNewPassword(''); setVerificationCode(''); }} className="text-sm font-semibold text-slate-600 hover:text-emerald-600 transition-colors">
                                    {t('auth.backToLogin')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <footer className="text-center py-4 sm:py-6 text-xs sm:text-sm text-slate-400">
                    © POMEN {new Date().getFullYear()}
                </footer>
            </div>
        </div>
    );
};

export default React.memo(LoginScreen);
